/**
 * Contract Bridge bot.
 *
 * Bidding is a simplified natural system built on point count:
 *  - Opening: 13+ points (or 12 with the rule of 20) opens its best suit, 1NT with 15–17 balanced, 2NT with 20–21;
 *    a seven-card suit with a weak hand preempts at the three level.
 *  - Everything after that works from an estimate of the partnership's combined points: its own points (with shortness
 *    counted once a fit is found) plus what its partner's calls have shown. It then bids the lowest legal bid in the
 *    best strain while that stays at or below the level the combined strength supports, jumping to game or slam when
 *    the strength is there, and passes once the auction has reached that level.
 *  - It overcalls with a good five-card suit, makes takeout doubles, and doubles opposing game contracts when its side
 *    clearly holds the majority of the high cards.
 * Play uses ordinary heuristics: lead from sequences or the fourth best of a long suit, second hand low, third hand high,
 * cover cheaply in fourth seat, ruff when an opponent is winning, draw trumps as declarer, and cash master cards. When
 * it is the declarer it plays the dummy's cards too, knowing both hands.
 * The bot only ever reads its own seat's view.
 */
import { buildDeck, parseCard, SUITS, type CardId, type Suit } from '../../core/cards.js';
import { chance, type BotContext, type BotEngine } from '../../bots/types.js';
import type { BridgeStrain } from './score.js';
import {
  bridgeAuctionInfo,
  bridgeBidAllowed,
  bridgeHcp,
  bridgePartnerOf,
  bridgeTeamOf,
  bridgeTrickWinner,
  bridgeTrump,
  type BridgeAction,
  type BridgeCall,
  type BridgeLegal,
  type BridgeView,
} from './engine.js';

const rank = (c: CardId) => parseCard(c).rank;
const suitOf = (c: CardId) => parseCard(c).suit;

// ───────────────────────────────────────────────────────────── bidding

interface Shape {
  len: Record<Suit, number>;
  hcp: number;
  balanced: boolean;
  lengthPts: number;
  shortPts: number;
}

function shapeOf(hand: readonly CardId[]): Shape {
  const len: Record<Suit, number> = { S: 0, H: 0, D: 0, C: 0 };
  for (const c of hand) len[suitOf(c)]++;
  const counts = SUITS.map((s) => len[s]);
  const singles = counts.filter((n) => n <= 1).length;
  const doubles = counts.filter((n) => n === 2).length;
  return {
    len,
    hcp: bridgeHcp(hand),
    balanced: singles === 0 && doubles <= 1,
    lengthPts: counts.reduce((n, l) => n + Math.max(0, l - 4), 0),
    shortPts: counts.reduce((n, l) => n + (l === 0 ? 3 : l === 1 ? 2 : l === 2 ? 1 : 0), 0),
  };
}

const isMajor = (s: BridgeStrain) => s === 'H' || s === 'S';

/** Highest-ranking suit among the longest (spades > hearts > diamonds > clubs). */
function longestSuit(sh: Shape, only?: readonly Suit[]): Suit {
  const order: Suit[] = ['S', 'H', 'D', 'C'];
  let best: Suit = (only ?? order)[0]!;
  for (const s of order) {
    if (only && !only.includes(s)) continue;
    if (sh.len[s] > sh.len[best] || (sh.len[s] === sh.len[best] && order.indexOf(s) < order.indexOf(best))) best = s;
  }
  return best;
}

/** Combined points needed to play at each level (the declarer play here is not perfect, so these are a little cautious). */
const GAME_AT = 28;
const SLAM_AT = 37;
const GRAND_AT = 41;

/** Highest level the partnership can support in a strain. */
function supportedLevel(combined: number, strain: BridgeStrain): number {
  if (combined >= GRAND_AT) return 7;
  if (combined >= SLAM_AT) return 6;
  if (combined >= GAME_AT) return strain === 'N' ? 3 : isMajor(strain) ? 4 : 5;
  if (strain === 'N') return combined >= 25 ? 2 : combined >= 20 ? 1 : 0;
  return combined >= 25 ? 3 : combined >= 22 ? 2 : combined >= 18 ? 1 : 0;
}

/** What the partner's calls tell us about their strength (a rough midpoint), or null when they have not bid. */
function partnerMidpoint(view: BridgeView): number | null {
  const me = view.seat!;
  const partner = bridgePartnerOf(me);
  const team = bridgeTeamOf(me);
  const calls = view.calls;
  const partnerBids = calls.filter((c) => c.seat === partner && c.call.kind === 'bid');
  const partnerDoubled = calls.some((c) => c.seat === partner && c.call.kind === 'double');
  if (partnerBids.length === 0) {
    if (partnerDoubled) return 14;
    // A pass shows little, especially once the auction has started.
    return calls.some((c) => c.seat === partner && c.call.kind === 'pass') ? 6 : null;
  }
  const first = partnerBids[0]!.call as Extract<BridgeCall, { kind: 'bid' }>;
  const firstIndex = calls.findIndex((c) => c.seat === partner && c.call.kind === 'bid');
  const myEarlierBid = calls.slice(0, firstIndex).find((c) => c.seat === me && c.call.kind === 'bid')?.call as Extract<BridgeCall, { kind: 'bid' }> | undefined;
  const oppBefore = calls.slice(0, firstIndex).some((c) => bridgeTeamOf(c.seat) !== team && c.call.kind === 'bid');
  let mid: number;
  if (!myEarlierBid) {
    if (oppBefore) mid = first.level === 1 ? (first.strain === 'N' ? 16 : 11) : 12;
    else if (first.strain === 'N') mid = first.level === 1 ? 16 : first.level === 2 ? 20.5 : 24;
    else mid = first.level <= 2 ? 14 : 8;
  } else if (first.strain === myEarlierBid.strain) {
    const steps = first.level - myEarlierBid.level;
    mid = steps <= 1 ? 7 : steps === 2 ? 10 : 12;
  } else if (first.strain === 'N') {
    mid = first.level === 1 ? 8 : first.level === 2 ? 11.5 : 14.5;
  } else {
    const over = first.level - myEarlierBid.level;
    mid = over <= 0 ? 9 : over === 1 ? 11 : 13;
  }
  // Each further constructive bid shows a little more.
  return Math.min(20, mid + Math.min(3, 1 * (partnerBids.length - 1)));
}

interface BidChoice {
  strain: BridgeStrain;
  /** Points to use for the partnership total (shortness counted when a suit fit exists). */
  pts: number;
}

function chooseStrain(view: BridgeView, sh: Shape, partnerMid: number | null): BidChoice {
  const me = view.seat!;
  const partner = bridgePartnerOf(me);
  const partnerBids = view.calls.filter((c) => c.seat === partner && c.call.kind === 'bid').map((c) => c.call as Extract<BridgeCall, { kind: 'bid' }>);
  const myBids = view.calls.filter((c) => c.seat === me && c.call.kind === 'bid').map((c) => c.call as Extract<BridgeCall, { kind: 'bid' }>);
  const base = sh.hcp + sh.lengthPts;
  // A fit in a suit the partner has bid: three or more cards to support it.
  for (let i = partnerBids.length - 1; i >= 0; i--) {
    const s = partnerBids[i]!.strain;
    if (s !== 'N' && sh.len[s as Suit] >= (isMajor(s) ? 3 : 4)) return { strain: s, pts: sh.hcp + sh.shortPts + Math.max(0, sh.len[s as Suit] - 4) };
  }
  // Their no trump: stay in no trump unless we hold a long suit of our own.
  const partnerNT = partnerBids.some((b) => b.strain === 'N');
  const long = longestSuit(sh);
  if (sh.len[long] >= 6 || (sh.len[long] >= 5 && isMajor(long) && !partnerNT)) return { strain: long, pts: base };
  if (partnerNT) return { strain: 'N', pts: sh.hcp };
  // A suit we have bid ourselves and still like.
  const mine = myBids.map((b) => b.strain).filter((s) => s !== 'N').find((s) => sh.len[s as Suit] >= 5);
  if (mine) return { strain: mine, pts: base };
  if (sh.len[long] >= 5) return { strain: long, pts: base };
  // A suit the partner bid in which we have a doubleton or better, else no trump.
  const psuit = partnerBids.map((b) => b.strain).find((s) => s !== 'N' && sh.len[s as Suit] >= 2);
  if (psuit && (partnerMid ?? 0) >= 13 && sh.len[psuit as Suit] >= 3) return { strain: psuit, pts: sh.hcp + sh.shortPts };
  return { strain: sh.balanced || partnerBids.length === 0 ? 'N' : long, pts: sh.hcp };
}

function lowestBid(lastBid: { level: number; strain: BridgeStrain } | null, strain: BridgeStrain): number {
  for (let level = 1; level <= 7; level++) if (bridgeBidAllowed(lastBid, level, strain)) return level;
  return 8;
}

function openingCall(sh: Shape, pts: number): BridgeCall {
  const sorted = SUITS.map((s) => sh.len[s]).sort((a, b) => b - a);
  const rule20 = sh.hcp >= 12 && sh.hcp + sorted[0]! + sorted[1]! >= 20;
  if (sh.balanced && sh.hcp >= 20 && sh.hcp <= 21) return { kind: 'bid', level: 2, strain: 'N' };
  if (sh.balanced && sh.hcp >= 22) return { kind: 'bid', level: 3, strain: 'N' };
  if (sh.balanced && sh.hcp >= 15 && sh.hcp <= 17) return { kind: 'bid', level: 1, strain: 'N' };
  if (pts >= 13 || rule20) {
    const majors: Suit[] = (['S', 'H'] as const).filter((s) => sh.len[s] >= 5);
    if (majors.length > 0) return { kind: 'bid', level: 1, strain: longestSuit(sh, majors) };
    let pick: Suit = sh.len.D > sh.len.C ? 'D' : sh.len.C > sh.len.D ? 'C' : sh.len.D >= 4 ? 'D' : 'C';
    if (sh.len.D === 3 && sh.len.C === 3) pick = 'C';
    return { kind: 'bid', level: 1, strain: pick };
  }
  const long = longestSuit(sh);
  if (sh.len[long] >= 7 && sh.hcp >= 5 && sh.hcp <= 10) return { kind: 'bid', level: sh.len[long] >= 8 ? 4 : 3, strain: long };
  return { kind: 'pass' };
}

function chooseCall(view: BridgeView, ctx: BotContext): BridgeCall {
  const me = view.seat!;
  const sh = shapeOf(view.hand);
  const noise = ctx.difficulty === 'BEGINNER' ? (ctx.rng.next() - 0.5) * 6 : ctx.difficulty === 'EASY' ? (ctx.rng.next() - 0.5) * 3 : 0;
  const bids = view.calls.filter((c) => c.call.kind === 'bid');
  const info = bridgeAuctionInfo(view.calls, me);
  const lastBid = info.lastBid;

  if (bids.length === 0) {
    return openingCall(sh, sh.hcp + sh.lengthPts + noise);
  }

  const partnerMid = partnerMidpoint(view);
  const partnerCalledDouble = view.calls.some((c) => c.seat === bridgePartnerOf(me) && c.call.kind === 'double');
  const mineTeam = bridgeTeamOf(me);
  const oppBids = bids.filter((c) => bridgeTeamOf(c.seat) !== mineTeam);
  const sideBids = bids.filter((c) => bridgeTeamOf(c.seat) === mineTeam);

  // Answering a takeout double: bid the best suit we hold that the opponents did not bid.
  if (partnerCalledDouble && sideBids.length === 0 && lastBid && bridgeTeamOf(lastBid.seat) !== mineTeam) {
    const oppStrains = new Set(oppBids.map((c) => (c.call as { strain: BridgeStrain }).strain));
    const options = SUITS.filter((s) => !oppStrains.has(s));
    const suit = longestSuit(sh, options.length ? options : undefined);
    const jump = sh.hcp >= 10;
    const base = lowestBid(lastBid, suit);
    return { kind: 'bid', level: Math.min(7, base + (jump ? 1 : 0)), strain: suit };
  }

  // Overcalls and takeout doubles when only the opponents have bid.
  if (sideBids.length === 0 && lastBid && bridgeTeamOf(lastBid.seat) !== mineTeam) {
    const oppSuit = lastBid.strain;
    const hcp = sh.hcp + noise;
    if (lastBid.level === 1 && info.canDouble && oppSuit !== 'N' && hcp >= 12 && sh.len[oppSuit as Suit] <= 2 && SUITS.every((s) => s === oppSuit || sh.len[s] >= 3)) {
      return { kind: 'double' };
    }
    const long = longestSuit(sh, SUITS.filter((s) => s !== oppSuit));
    if (sh.balanced && hcp >= 15 && hcp <= 18 && lastBid.level === 1 && bridgeBidAllowed(lastBid, 1, 'N') && oppSuit !== 'N') return { kind: 'bid', level: 1, strain: 'N' };
    const level = lowestBid(lastBid, long);
    if (sh.len[long] >= 5 && hcp >= (level === 1 ? 8 : 11) && hcp <= 17 && level <= 2) return { kind: 'bid', level, strain: long };
    if (sh.len[long] >= 6 && hcp >= 6 && hcp <= 11 && level <= 3 && lastBid.level <= 1) return { kind: 'bid', level: Math.max(level, 2), strain: long };
    if (hcp >= 19 && lastBid.level <= 3) return { kind: 'double' };
    return { kind: 'pass' };
  }

  // Doubling a game or slam the opponents have stretched to.
  const partnerGuess = partnerMid ?? 9;
  if (info.canDouble && lastBid) {
    const stretched = lastBid.level >= 4 || (lastBid.level === 3 && lastBid.strain === 'N');
    if (stretched && sh.hcp + partnerGuess - 2 >= 22 && ctx.difficulty !== 'BEGINNER') return { kind: 'double' };
  }

  const choice = chooseStrain(view, sh, partnerMid);
  const combined = choice.pts + partnerGuess + noise;
  // Game in a minor needs eleven tricks: prefer three no trump unless the suit is very long.
  if ((choice.strain === 'C' || choice.strain === 'D') && sh.len[choice.strain] < 7 && combined >= GAME_AT && combined < SLAM_AT && bridgeBidAllowed(lastBid, 3, 'N')) choice.strain = 'N';
  const target = supportedLevel(combined, choice.strain);
  const lowest = lowestBid(lastBid, choice.strain);

  // Already high enough on our side: let it ride.
  if (lastBid && bridgeTeamOf(lastBid.seat) === mineTeam && lastBid.level >= target) return { kind: 'pass' };
  if (lowest > 7 || lowest > target) return { kind: 'pass' };

  let level = lowest;
  // Jump to game (or slam) once the combined strength is there and nobody has gone past it.
  const game = choice.strain === 'N' ? 3 : isMajor(choice.strain) ? 4 : 5;
  if (combined >= GAME_AT && combined < SLAM_AT && lowest < game && bridgeBidAllowed(lastBid, game, choice.strain)) level = game;
  else if (combined >= SLAM_AT && target >= 6) level = Math.max(lowest, target);
  if (!bridgeBidAllowed(lastBid, level, choice.strain)) level = lowest;
  return { kind: 'bid', level, strain: choice.strain };
}

// ───────────────────────────────────────────────────────────── play

interface Knowledge {
  unseen: Set<CardId>;
}

function buildKnowledge(view: BridgeView, ctx: BotContext): Knowledge {
  const seen = new Set<CardId>(view.hand);
  view.dummyHand?.forEach((c) => seen.add(c));
  view.trick.forEach((p) => seen.add(p.card));
  const remembered = ctx.profile.memory;
  view.tricks.forEach((t, i) => {
    if (remembered >= 1 || i >= view.tricks.length - Math.max(1, Math.round(view.tricks.length * remembered))) {
      for (const p of t.plays) seen.add(p.card);
    }
  });
  return { unseen: new Set(buildDeck().filter((c) => !seen.has(c))) };
}

/** No unseen card of the suit is higher (cards in our own side's hands do not count). */
function isMaster(card: CardId, k: Knowledge): boolean {
  const s = suitOf(card);
  for (const u of k.unseen) if (suitOf(u) === s && rank(u) > rank(card)) return false;
  return true;
}

const lowestOf = (cards: CardId[]) => [...cards].sort((a, b) => rank(a) - rank(b))[0]!;
const highestOf = (cards: CardId[]) => [...cards].sort((a, b) => rank(b) - rank(a))[0]!;

/** The lowest card of a group of touching honours/equals that beat `than`, preferring the cheapest winner. */
function cheapestWinner(cards: CardId[], beats: (c: CardId) => boolean): CardId | null {
  const winners = cards.filter(beats);
  return winners.length > 0 ? lowestOf(winners) : null;
}

function chooseLead(hand: readonly CardId[], cards: CardId[], view: BridgeView, k: Knowledge | null, trump: Suit | null, declaringSide: boolean): CardId {
  const bySuit = (s: Suit) => cards.filter((c) => suitOf(c) === s).sort((a, b) => rank(b) - rank(a));
  const sh = shapeOf(hand);
  const side = SUITS.filter((s) => s !== trump && bySuit(s).length > 0);
  const trumpsLeft = trump && k ? [...k.unseen].filter((u) => suitOf(u) === trump).length : 0;

  // Declarer: draw trumps while the opponents still hold some and we hold strength in the suit.
  if (declaringSide && trump && trumpsLeft > 0) {
    const t = bySuit(trump);
    if (t.length > 0 && (rank(t[0]!) >= 12 || t.length >= 4)) return t[0]!;
  }
  // Cash master cards in side suits once trumps are gone (or in no trump).
  if (k && (declaringSide ? trumpsLeft === 0 || !trump : true)) {
    const masters = cards.filter((c) => suitOf(c) !== trump && isMaster(c, k));
    if (masters.length > 0 && (declaringSide || !trump || trumpsLeft === 0)) return highestOf(masters);
  }
  if (!declaringSide) {
    // Defenders: lead partner's suit when they bid one, else a sequence, else the fourth best of the longest suit.
    const partner = bridgePartnerOf(view.seat!);
    const pSuit = view.calls.find((c) => c.seat === partner && c.call.kind === 'bid') as { call: { strain: BridgeStrain } } | undefined;
    if (pSuit && pSuit.call.strain !== 'N' && pSuit.call.strain !== trump && bySuit(pSuit.call.strain as Suit).length > 0 && sh.len[pSuit.call.strain as Suit] > 0) {
      const cs = bySuit(pSuit.call.strain as Suit);
      return cs.length >= 3 ? lowestOf(cs.slice(0, 3)) : cs[0]!;
    }
    for (const s of side) {
      const cs = bySuit(s);
      if (cs.length >= 2 && rank(cs[0]!) >= 11 && rank(cs[0]!) - rank(cs[1]!) === 1) return cs[0]!;
      if (cs.length >= 2 && rank(cs[0]!) === 14 && rank(cs[1]!) === 13) return cs[0]!;
    }
    const longest = [...side].sort((a, b) => bySuit(b).length - bySuit(a).length || rank(bySuit(b)[0]!) - rank(bySuit(a)[0]!))[0];
    if (longest) {
      const cs = bySuit(longest);
      if (cs.length >= 4) return cs[3]!;
      return cs.length === 3 ? cs[2]! : cs[cs.length - 1]!;
    }
  } else {
    // Declarer: play low from the longest side suit toward strength.
    const longest = [...side].sort((a, b) => bySuit(b).length - bySuit(a).length)[0];
    if (longest) return lowestOf(bySuit(longest));
  }
  return lowestOf(cards);
}

function choosePlay(view: BridgeView, legal: Extract<BridgeLegal, { kind: 'play' }>, ctx: BotContext): CardId {
  const cards = legal.cards;
  if (cards.length === 1) return cards[0]!;
  const me = view.seat!;
  const from = legal.from;
  const contract = view.contract!;
  const trump = bridgeTrump(contract.strain);
  const hand = from === me ? view.hand : (view.dummyHand ?? view.hand);
  const k = ctx.profile.memory > 0 ? buildKnowledge(view, ctx) : null;
  const trick = view.trick;
  const team = bridgeTeamOf(from);
  const declaringSide = team === bridgeTeamOf(contract.declarer);

  if (trick.length === 0) return chooseLead(hand, cards, view, k, trump, declaringSide);

  const winnerSeat = bridgeTrickWinner(trick, trump);
  const winnerCard = trick.find((p) => p.seat === winnerSeat)!.card;
  const partnerWinning = bridgeTeamOf(winnerSeat) === team;
  const lead = suitOf(trick[0]!.card);
  const wc = parseCard(winnerCard);
  const beats = (c: CardId) => {
    const p = parseCard(c);
    return (trump !== null && p.suit === trump && wc.suit !== trump) || (p.suit === wc.suit && p.rank > wc.rank);
  };
  const followers = cards.filter((c) => suitOf(c) === lead);

  if (followers.length > 0) {
    if (partnerWinning) {
      // Partner has it: stay low unless a later opponent could still overtake with a card we can beat now.
      if (trick.length === 3) return lowestOf(followers);
      const safe = k ? isMaster(winnerCard, k) : rank(winnerCard) === 14;
      if (safe) return lowestOf(followers);
      if (trick.length === 2) {
        const over = cheapestWinner(followers, beats);
        if (over && rank(over) >= 11) return over;
      }
      return lowestOf(followers);
    }
    const win = cheapestWinner(followers, beats);
    if (trick.length === 1) {
      // Second hand low — but take the trick with a touching high card when it is clearly cheap and safe.
      if (win && k && isMaster(win, k)) return win;
      return lowestOf(followers);
    }
    if (win) return win;
    return lowestOf(followers);
  }

  // Out of the suit led: ruff when an opponent is winning, otherwise discard.
  const trumps = trump ? cards.filter((c) => suitOf(c) === trump) : [];
  if (!partnerWinning && trumps.length > 0) {
    const ruff = cheapestWinner(trumps, beats);
    const worth = rank(winnerCard) >= 12 || trick.length === 3 || (k !== null && isMaster(winnerCard, k));
    if (ruff && worth) return ruff;
  }
  // Discard the lowest card from the longest side suit, keeping winners and trumps.
  const sideCards = cards.filter((c) => suitOf(c) !== trump);
  const pool = sideCards.length > 0 ? sideCards : cards;
  const lens = new Map<Suit, number>();
  for (const c of hand) lens.set(suitOf(c), (lens.get(suitOf(c)) ?? 0) + 1);
  const ordered = [...pool].sort((a, b) => (lens.get(suitOf(b)) ?? 0) - (lens.get(suitOf(a)) ?? 0) || rank(a) - rank(b));
  const longSuit = suitOf(ordered[0]!);
  return lowestOf(pool.filter((c) => suitOf(c) === longSuit));
}

export const bridgeBot: BotEngine<BridgeView, BridgeLegal, BridgeAction> = {
  decideAction(view, legal, ctx) {
    if (legal.kind === 'bid') return this.chooseBet!(view, legal, ctx);
    if (legal.kind === 'play') return this.chooseCard!(view, legal, ctx);
    throw new Error('Bot asked to act without legal actions');
  },

  chooseBet(view, legal, ctx) {
    if (legal.kind !== 'bid') return this.handleTimeout(view, legal, ctx);
    let call: BridgeCall;
    try {
      call = chooseCall(view, ctx);
    } catch {
      call = { kind: 'pass' };
    }
    // Never return an illegal call: fall back to passing.
    const info = bridgeAuctionInfo(view.calls, view.seat!);
    if (call.kind === 'bid' && !bridgeBidAllowed(info.lastBid, call.level, call.strain)) call = { kind: 'pass' };
    if (call.kind === 'double' && !info.canDouble) call = { kind: 'pass' };
    if (call.kind === 'redouble' && !info.canRedouble) call = { kind: 'pass' };
    return { type: 'call', call };
  },

  chooseCard(view, legal, ctx) {
    if (legal.kind !== 'play') return this.handleTimeout(view, legal, ctx);
    if (legal.cards.length === 1) return { type: 'play', card: legal.cards[0]! };
    if (chance(ctx, ctx.profile.mistakeRate)) return { type: 'play', card: legal.cards[ctx.rng.int(legal.cards.length)]! };
    const card = choosePlay(view, legal, ctx);
    return { type: 'play', card: legal.cards.includes(card) ? card : legal.cards[0]! };
  },

  evaluateState(view) {
    const me = view.seat;
    if (me === null) return 0;
    const team = me % 2;
    const diff = view.teamScores[team]! - view.teamScores[1 - team]!;
    return Math.max(0, Math.min(1, 0.5 + diff / 3000));
  },

  calculateRisk(view, legal) {
    if (legal.kind === 'bid') return Math.min(1, bridgeHcp(view.hand) / 40);
    return 0;
  },

  handleTimeout(view, legal) {
    if (legal.kind === 'bid') return { type: 'call', call: { kind: 'pass' } };
    if (legal.kind === 'play') return { type: 'play', card: lowestOf(legal.cards) };
    throw new Error('No legal action');
  },
};

